import { Component, Injector, input, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import moment, { Moment, MomentInput } from 'moment/moment';
import { MatTooltip } from '@angular/material/tooltip';
import { COIN_FORMATS, DefaultFormValueControl } from '@coin/shared/core/util-helpers';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatFormFieldClass } from '../../input.types';

@Component({
  selector: 'coin-month-input',
  imports: [
    FormsModule,
    MatDatepicker,
    MatDatepickerInput,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    MatIcon,
    TranslateModule,
    MatTooltip
  ],
  standalone: true,
  templateUrl: './month-input.component.html',
  styleUrl: './month-input.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: MAT_DATE_FORMATS,
      useFactory: (injector: Injector) => {
        const inheritedFormats = injector.get(MAT_DATE_FORMATS, null, { skipSelf: true, optional: true });
        const display = inheritedFormats?.display ?? COIN_FORMATS.display;
        const dateInput = (display.dateInput as string).replaceAll(/d+[./]/gi, '');
        return { ...COIN_FORMATS, display: { ...display, dateInput } };
      },
      deps: [Injector]
    }
  ]
})
export class MonthInputComponent extends DefaultFormValueControl<Moment> {
  readonly label = input<string>();
  readonly placeholder = input<string>();
  readonly matFormFieldClass = input<MatFormFieldClass>('customer-grey');
  readonly boldValue = input(false);
  readonly min = input<Moment>();
  readonly max = input<Moment>();

  readonly updateOn = input<'input' | 'blur'>('input');

  protected override toViewValue(value: MomentInput): Moment {
    return this.isInvalidDate(value) ? null : moment(value ?? null).utc();
  }

  protected onInputBlur(value: string) {
    if (this.updateOn() === 'blur' && !this.isSame(value)) {
      this.updateValue(moment(value));
    }
    this.markAsTouched();
  }

  protected onInputChange(value: string) {
    if (this.updateOn() === 'input' && !this.isSame(value)) {
      this.updateValue(moment(value));
    }
  }

  public isSame(value: MomentInput): boolean {
    if (!value && !this.viewValue()) return true;
    return !!moment(this.viewValue())?.isSame(value, 'month');
  }

  public setMonthAndYear(selectedDate: Moment, datepicker: MatDatepicker<Moment>) {
    const firstOfMonth = moment(this.viewValue());
    firstOfMonth.month(selectedDate.month());
    firstOfMonth.year(selectedDate.year());
    firstOfMonth.startOf('month');

    this.updateValue(firstOfMonth);
    datepicker.close();
  }

  private isInvalidDate(value: MomentInput): boolean {
    // BE sometimes populates empty date fields with this default date string, causing the dates to appear as though they have a valid value.
    if (moment(value).toISOString() === moment('0001-01-01T00:00:00.000Z').toISOString()) {
      console.warn('Invalid date string provided!');
      return true;
    }
    return false;
  }
}
